import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { loadIndustries } from "@/lib/industries";
import { industryIcon as iconForItem } from "@/lib/industryIcon";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import SafeImg from "@/components/common/SafeImg";
import { HOME_WRAP } from "./homeTheme";

function iconFor(item) {
  return iconForItem({ name: item.title, icon: item.icon });
}

/**
 * "Engineered for Industry" — deep-navy section. Tiles come from the verified Industries
 * collection (the same data as /industries and the mega menu); the homepage CMS list
 * (`data.industries`) is only the fallback if that collection can't load. Each tile links to
 * its industry page. No per-tile description is shown here.
 */
export default function IndustriesSection({ data }) {
  const section = data?.industriesSection || {};
  const cmsItems = (Array.isArray(data?.industries) ? data.industries : []).filter((i) => i.status !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const [pages, setPages] = useState([]);
  useEffect(() => {
    loadIndustries().then(setPages).catch(() => setPages([]));
  }, []);
  // The verified Industries collection is the source of truth; the homepage CMS list is only the fallback.
  const items = pages.length ? pages.map((p) => ({ title: p.name, slug: p.slug, icon: p.icon, image: p.image })) : cmsItems;
  if (!items.length) return null;

  const hrefFor = (item) => (item.slug ? `/industries/${item.slug}` : item.link || "/industries");

  return (
    // Shorter bottom padding: the Global Presence map follows on the same navy and continues this story.
    <section className="relative isolate overflow-hidden bg-[#071B3A] pb-14 pt-20 lg:pb-16 lg:pt-28" aria-labelledby="home-industries-title">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#1769FF]/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_70%_60%_at_80%_0%,#000,transparent)]" />
      </div>
      <div className={HOME_WRAP}>
        <SectionHeader
          tone="dark"
          eyebrow={section.title || "Engineered for Industry"}
          title={section.subtitle || "Powering the World's Critical Industries"} titleId="home-industries-title"
          action={
            <Link to={section.cta?.link || "/industries"} className="group inline-flex shrink-0 items-center gap-2 pb-1 text-sm font-semibold text-white">
              <span className="border-b border-white/30 pb-0.5 transition-colors group-hover:border-white">{section.cta?.label || "View All Industries"}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          }
          className="mb-12 lg:mb-16"
        />
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-5">
          {items.map((item, i) => {
            const Icon = iconFor(item);
            return (
              <Reveal as="li" key={`${item.title}-${i}`} delay={(i % 5) * 0.05} y={18}>
                <Link
                  to={hrefFor(item)}
                  className="group relative isolate flex h-full min-h-[190px] flex-col justify-between overflow-hidden rounded-2xl bg-white/[0.045] p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82FF] sm:min-h-[220px] sm:p-6"
                >
                  {item.image && (
                    <>
                      <SafeImg src={toAbsoluteUrl(item.image)} alt="" width="640" height="480" loading="lazy" decoding="async" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
                      <span aria-hidden="true" className="absolute inset-0 -z-10 bg-gradient-to-t from-[#071B3A] via-[#071B3A]/55 to-[#071B3A]/20" />
                    </>
                  )}
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.07] text-[#7FB0FF] transition-colors duration-300 group-hover:bg-[#1769FF] group-hover:text-white">
                    <Icon className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block font-heading text-[17px] font-medium leading-snug text-white sm:text-lg">{item.title}</span>
                    {item.description && <span className="mt-1.5 block text-[13px] leading-relaxed text-white/60">{item.description}</span>}
                  </span>
                  <ArrowUpRight className="absolute right-5 top-5 h-4 w-4 -translate-x-1 translate-y-1 text-white/0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:text-white/70" aria-hidden="true" />
                </Link>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
